import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { Button, Typography, Popover, IconButton } from '@mui/material';
import { productAssessmentQuestion } from 'api/trade_in';
import styles from '../TradeInFormModal.module.scss';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { ROUTES } from 'config/constants';

const Page2 = ({ productId, questionnaireAnswers, setQuestionnaireAnswers, setDeviceConditionString, setProductAssessmentFormCompleted }) => {
    const [questions, setQuestions] = useState([]);

    const handleAnswerClick = (questionId, specificationValue) => {
        setQuestionnaireAnswers((prevAnswers) => ({
            ...prevAnswers,
            [questionId]: {
                value: specificationValue.value,
                appCode: specificationValue.appCode
            }
        }));
    };

    const updateProductAssessmentAnswers = () => {
        const appCodes = Object.values(questionnaireAnswers).reduce((acc, answer) => {
            if (answer.appCode) acc.push(answer.appCode);

            return acc;
        }, []);

        setDeviceConditionString(appCodes.join(';'));
    };

    const checkAllQuestionsAnswered = () => {
        const allQuestionsAnswered = Object.values(questionnaireAnswers).every((answer) => answer.value !== null);

        setProductAssessmentFormCompleted(allQuestionsAnswered);
    };

    useEffect(() => {
        productAssessmentQuestion(productId).then((res) => {
            const initialAnswers = res.reduce((acc, question) => {
                if (question.specificationId !== null) {
                    acc[question.specificationId] = {
                        value: null, // to be set to boolean
                        appCode: null
                    };
                }

                return acc;
            }, {});

            setQuestions(res.filter((question) => question.specificationValue.length > 0));
            !questionnaireAnswers && setQuestionnaireAnswers(initialAnswers);
        });
    }, []);

    useEffect(() => {
        if (!questionnaireAnswers) return;

        updateProductAssessmentAnswers();
        checkAllQuestionsAnswered();
    }, [questionnaireAnswers]);

    if (!questions.length || !questionnaireAnswers) return <Spinner color='primary' />;

    return (
        <>
            <fieldset className={styles.page2}>
                {questions.map((question, index) => {
                    const hasQuestions = question.specificationValue.length > 0;
                    const labelStyle = hasQuestions ? styles.question : styles.noQuestions;

                    return (
                        <>
                            <div key={index} className={styles.formRow}>
                                <label className={labelStyle}>{question.specificationName}</label>
                                {
                                    question.specificationValue.map((specificationValue, index) => {
                                        const classStyle = questionnaireAnswers[question.specificationId]?.value === specificationValue.value ? `${styles.selected}` : '';

                                        return (
                                            <Button key={index} variant='outlined' size='small'
                                                disableRipple={true} className={classStyle} onClick={() => handleAnswerClick(question.specificationId, specificationValue)}>
                                                {specificationValue.value}
                                            </Button>
                                        );
                                    })
                                }
                                <LinePopover value={question.description} />
                            </div>
                            <hr />
                        </>
                    );
                })}
                <p className={styles.footer}>
                    Please note charges may apply if you fail to send us your device
                    within the stipulated time period. Please refer to the{' '}
                    <Link
                        className={styles.link}
                        to={ROUTES.TERMS_AND_CONDITIONS}
                        target='_blank'>
                        Terms & Conditions
                    </Link>{' '}
                    for more information. We will use your personal data in accordance
                    with our{' '}
                    <Link
                        className={styles.link}
                        to={ROUTES.PRIVACY_POLICY}
                        target='_blank'>
                        Privacy Policy
                    </Link>
                    .
                </p>
            </fieldset>
        </>
    );
};

const LinePopover = ({ value }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    if (!value) return null;

    return (
        <>
            <IconButton aria-describedby={id} className={styles.popoverButton} size='small'
                onClick={handleClick}>
                <QuestionCircleOutlined />
            </IconButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}>
                <Typography sx={{ p: 2 }} style={{ fontFamily: 'inherit', fontSize: 'inherit' }}>{value}</Typography>
            </Popover>
        </>
    );
};

export default Page2;

Page2.propTypes = {
    productId: PropTypes.number.isRequired,
    questionnaireAnswers: PropTypes.object,
    setQuestionnaireAnswers: PropTypes.func.isRequired,
    setDeviceConditionString: PropTypes.func.isRequired,
    setProductAssessmentFormCompleted: PropTypes.func.isRequired
};

LinePopover.propTypes = {
    value: PropTypes.string.isRequired
};
